import * as React from 'react'
import { isFragment } from 'react-is'
import clsx from 'clsx'
import { MenuList, MenuListProps, ActionRefType } from '../MenuList'
import { Popover, PopoverOrigin, PopoverProps } from '../Popover'
import { TransitionProps } from '../transitions'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'

export interface MenuClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the Paper component. */
  paper: string
  /** Styles applied to the List component via `MenuList`. */
  list: string
}

export interface MenuProps extends PopoverProps {
  /**
   * An HTML element, or a function that returns one.
   * It's used to set the position of the menu.
   */
  anchorEl?: PopoverProps['anchorEl']
  /**
   * If `true` (Default) will focus the `[role="menu"]` if no focusable child is found. Disabled
   * children are not focusable. If you set this prop to `false` focus will be placed
   * on the parent modal container. This has severe accessibility implications
   * and should only be considered if you manage focus otherwise.
   * @default true
   */
  autoFocus?: boolean
  /**
   * Menu contents, normally `MenuItem`s.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<MenuClasses>
  /**
   * When opening the menu will not focus the active item but the `[role="menu"]`
   * unless `autoFocus` is also set to `false`. Not using the default means not
   * following WAI-ARIA authoring practices. Please be considerate about possible
   * accessibility implications.
   * @default false
   */
  disableAutoFocusItem?: boolean
  /**
   * Props applied to the [`MenuList`](/material-ui/api/menu-list/) element.
   * @default {}
   */
  MenuListProps?: Partial<MenuListProps>
  /**
   * Callback fired when the component requests to be closed.
   *
   * @param {object} event The event source of the callback.
   * @param {string} reason Can be: `"escapeKeyDown"`, `"backdropClick"`, `"tabKeyDown"`.
   */
  onClose?: PopoverProps['onClose']
  /**
   * If `true`, the component is shown.
   */
  open: boolean
  /**
   * `classes` prop applied to the [`Popover`](/material-ui/api/popover/) element.
   */
  PopoverClasses?: PopoverProps['classes']
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The length of the transition in `ms`, or 'auto'
   * @default 'auto'
   */
  transitionDuration?: TransitionProps['timeout'] | 'auto'
  /**
   * Props applied to the transition element.
   * By default, the element is based on this [`Transition`](http://reactcommunity.org/react-transition-group/transition/) component.
   * @default {}
   */
  TransitionProps?: TransitionProps
  /**
   * The variant to use. Use `menu` to prevent selected items from impacting the initial focus.
   * @default 'selectedMenu'
   */
  variant?: 'menu' | 'selectedMenu'
  direction?: 'ltr' | 'rtl'
  /**
   * @ignore - internal props
   */
  renderContentTop?: JSX.Element
  /**
   * @ignore - internal props
   */
  renderContentBottom?: JSX.Element
}

const RTL_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
}

const LTR_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'left',
}

export const Menu = React.forwardRef<HTMLDivElement, MenuProps>(
  function Menu(props, ref) {
    const {
      autoFocus = true,
      children,
      classes,
      disableAutoFocusItem = false,
      MenuListProps = {},
      onClose,
      open,
      PaperProps = {},
      PopoverClasses,
      transitionDuration = 'auto',
      TransitionProps: { onEntering, ...TransitionProps } = {},
      variant = 'selectedMenu',
      direction,
      sx,
      renderContentTop,
      renderContentBottom,
      ...other
    } = props

    const isRtl = direction === 'rtl'

    const autoFocusItem = autoFocus && !disableAutoFocusItem && open

    const menuListActionsRef = React.useRef<ActionRefType>(null)

    const handleEntering = (element: HTMLElement, isAppearing: boolean) => {
      if (menuListActionsRef.current) {
        // @ts-ignore
        menuListActionsRef.current.adjustStyleForScrollbar(element, direction)
      }

      if (onEntering) {
        onEntering(element, isAppearing)
      }
    }

    const handleListKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault()

        if (onClose) {
          // @ts-ignore
          onClose(event, 'tabKeyDown')
        }
      }
    }

    /**
     * the index of the item should receive focus
     * in a `variant="selectedMenu"` it's the first `selected` item
     * otherwise it's the very first item.
     */
    let activeItemIndex = -1
    // since we inject focus related props into children we have to do a lookahead
    // to check if there is a `selected` item. We're looking for the last `selected`
    // item and use the first valid item as a fallback
    React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) {
        return
      }

      if (process.env.NODE_ENV !== 'production') {
        if (isFragment(child)) {
          console.error(
            [
              "The Menu component doesn't accept a Fragment as a child.",
              'Consider providing an array instead.',
            ].join('\n')
          )
        }
      }

      if (!child.props.disabled) {
        if (variant === 'selectedMenu' && child.props.selected) {
          activeItemIndex = index
        } else if (activeItemIndex === -1) {
          activeItemIndex = index
        }
      }
    })

    return (
      <Popover
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'right' : 'left',
        }}
        transformOrigin={isRtl ? RTL_ORIGIN : LTR_ORIGIN}
        PaperProps={{
          ...PaperProps,
          classes: {
            ...PaperProps.classes,
            root: clsx(classes?.paper, style.paper),
          },
        }}
        className={clsx(classes?.root, sxFunc(sx))}
        open={open}
        ref={ref}
        transitionDuration={transitionDuration}
        TransitionProps={{ onEntering: handleEntering, ...TransitionProps }}
        {...other}
        classes={PopoverClasses}
      >
        {renderContentTop && renderContentTop}
        <MenuList
          onKeyDown={handleListKeyDown}
          actions={menuListActionsRef}
          /* eslint-disable-next-line */
          autoFocus={
            autoFocus && (activeItemIndex === -1 || disableAutoFocusItem)
          }
          autoFocusItem={autoFocusItem}
          variant={variant}
          {...MenuListProps}
          className={clsx(classes?.list, MenuListProps.className)}
        >
          {children}
        </MenuList>
        {renderContentBottom && renderContentBottom}
      </Popover>
    )
  }
)
